import { useCallback, useEffect, useRef, useState } from "react";
import { Satellite, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const PHASE = {
  scanning: "🔎 scanning for the best pair",
  waiting_calm: "🟢 green-only gate — waiting for CALM skies (normal 🟡 skipped)",
  placing: "🛒 buying the flight",
  flying: "🚀 in flight",
  daily_guard: "🚨 circuit-breaker — 5h cool-down, then auto-resumes with a smaller session (deposits unlock instantly)",
  deriv_daily_limit: "🛃 Deriv's own daily stake limit hit — auto-resumes after UTC midnight (raise it: Deriv → Settings → Self-exclusion / Account limits)",
  idle: "idle",
};

export const RocketServerBot = ({ authorized, acct, token, currency = "USD", locked, onStatus }) => {
  const pairShort = (s) => (s ? String(s).replace(/^1HZ(\d+)V$/, "V$1·1s").replace(/^R_(\d+)$/, "V$1") : "");
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stakePct, setStakePct] = useState(() => {
    const v = parseFloat(localStorage.getItem("rocketSrvPct") || "2");
    return [1, 2, 3, 5, 8, 10].includes(v) ? v : 2;
  });
  // 💵 stake mode: "pct" (% of balance, compounds) or "fixed" (flat $ per flight)
  const [stakeMode, setStakeMode] = useState(() => (localStorage.getItem("rocketSrvStakeMode") === "fixed" ? "fixed" : "pct"));
  const [fixedStake, setFixedStake] = useState(() => localStorage.getItem("rocketSrvFixed") || "1");
  const [slPct, setSlPct] = useState(() => {
    const v = parseFloat(localStorage.getItem("rocketSrvSlPct") || "40");
    return [30, 40, 50].includes(v) ? v : 40;
  });
  const [mode, setMode] = useState(() => {
    const v = localStorage.getItem("rocketSrvMode") || "earner";
    return ["steady", "earner"].includes(v) ? v : "earner";
  });
  // 🎯 Comeback defaults ON — people forget to select it; risk-light by default
  const [comeback, setComeback] = useState(() => localStorage.getItem("rocketSrvComeback") !== "0");
  // 🏦 Secure stops default ON — bank profits; OFF = true non-stop 24/7
  const [secureStops, setSecureStops] = useState(() => localStorage.getItem("rocketSrvSecure") !== "0");
  const [ptPct, setPtPct] = useState(() => {
    const v = parseFloat(localStorage.getItem("rocketSrvPt") || "15");
    return [0, 10, 15, 25].includes(v) ? v : 15;
  });
  const running = !!st?.running;

  // keep the callback in a ref — an inline prop must NOT retrigger the poll
  // effect on every parent render (that caused a fetch→render→fetch loop and
  // made the whole page "jump")
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  const refresh = useCallback(async () => {
    if (!acct || acct === "none") return;
    try {
      const { data } = await api.get(`/rocket-bot/status/${acct}`);
      setSt(data);
      onStatusRef.current?.(data);
    } catch { /* transient */ }
  }, [acct]);

  useEffect(() => {
    if (!authorized) return undefined;
    refresh();
    const iv = setInterval(refresh, running ? 6000 : 15000);
    return () => clearInterval(iv);
  }, [authorized, refresh, running]);

  const toggle = async (on) => {
    if (busy) return;
    setBusy(true);
    try {
      if (on) {
        const isDemo = /^(VRTC|VRW|DOT)/i.test(acct);
        const fixedVal = Math.max(1, Number(fixedStake) || 1);
        const stakeDesc = stakeMode === "fixed" ? `a flat $${fixedVal.toFixed(2)}` : `${stakePct}% of your REAL balance`;
        if (!isDemo && !window.confirm(
          `LIVE ACCOUNT — the 24/7 rocket stakes ${stakeDesc} per flight (banks between 1.02–1.20x) and keeps flying with your browser closed. Fly live?`)) {
          setBusy(false);
          return;
        }
        await api.post("/rocket-bot/start", {
          account_id: acct, access_token: token || "", growth: 0.01,
          stake_pct: stakePct, sl_pct: slPct, target_mode: mode, comeback,
          fixed_stake: stakeMode === "fixed" ? fixedVal : 0,
          profit_target_pct: ptPct,
          secure_stops: secureStops,
          disclaimer_accepted: true,
        });
        toast.success("🛰️ 24/7 mode ON — the rocket now flies server-side, even with this tab closed.");
      } else {
        await api.post(`/rocket-bot/stop/${acct}`);
        toast("🛰️ 24/7 mode OFF — server rocket landed.");
      }
      await refresh();
    } catch (e) {
      toast.error(on ? "Couldn't start 24/7 mode" : "Couldn't stop 24/7 mode",
        { description: e?.response?.data?.detail || e?.message || String(e) });
    }
    setBusy(false);
  };

  if (!authorized) return null;
  const paused = st?.paused_until && !running;
  const recent = (st?.recent_trades || []).slice(0, 10);

  return (
    <div data-testid="rocket-server-card" className={`vts-card p-4 space-y-2.5 ${running ? "border border-vts-profit/40" : ""}`}>
      <div className="flex items-center gap-2">
        <Satellite className={`h-4 w-4 ${running ? "text-vts-profit" : "text-vts-blue"}`} />
        <span className="text-sm font-semibold text-white">24/7 Server Mode</span>
        {running && (
          <span data-testid="rocket-server-live-chip" className="px-1.5 py-0.5 rounded bg-vts-profit/15 text-vts-profit text-[9px] font-bold font-mono animate-pulse">
            ● LIVE ON SERVER
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-vts-silver/60" />}
          <Switch data-testid="rocket-server-toggle" checked={running} disabled={busy || !!locked || !!paused}
            onCheckedChange={toggle} />
        </div>
      </div>
      <p className="text-[10px] text-vts-silver/55 font-mono leading-snug">
        Flies from our servers — <b className="text-white">close the app or your phone, the rocket keeps going</b>. Stake = your chosen
        % of the live balance every flight (compounding), banks <b className="text-white">anywhere between 1.02–1.20x</b> (the exit is
        re-rolled every flight — never glued to 1.3), always on the ⭐ best pair and <b className="text-white">ONLY when it reads 🟢 CALM</b> (🟡 normal skies are skipped — that's where spikes hide). Restarts <b className="text-white">immediately after every
        flight — wins AND losses</b>. 🏦 <b className="text-white">Trailing profit lock:</b> no ceiling — it can flip the account +100%+,
        but if the run gives back 35% of its peak profit it STOPS and secures the rest. 🚨 <b className="text-white">Daily circuit-breaker</b> on
    real accounts: down 60% today across ALL flights (local + server) → grounded until the next UTC day.
      </p>
      {!running && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Stake per flight</span>
            {[["pct", "% OF BALANCE"], ["fixed", "FLAT $"]].map(([m, lbl]) => (
              <button key={m} data-testid={`rocket-server-stakemode-${m}`} disabled={busy}
                onClick={() => { setStakeMode(m); try { localStorage.setItem("rocketSrvStakeMode", m); } catch { /* quota */ } }}
                className={`rounded-md border px-2.5 py-1 text-[9px] font-bold font-mono transition ${
                  stakeMode === m ? "border-vts-profit bg-vts-profit/15 text-vts-profit" : "border-vts-line bg-vts-surface text-vts-silver/60 hover:text-white"}`}>
                {lbl}
              </button>
            ))}
            {stakeMode === "pct" ? (
              <>
                {[1, 2, 3, 5, 8, 10].map((p) => (
                  <button key={p} data-testid={`rocket-server-pct-${p}`} disabled={busy}
                    onClick={() => { setStakePct(p); try { localStorage.setItem("rocketSrvPct", String(p)); } catch { /* quota */ } }}
                    className={`rounded-md border px-3 py-1.5 text-[11px] font-bold font-mono transition ${
                      stakePct === p ? "border-vts-blue bg-vts-blue text-white" : "border-vts-line bg-vts-surface text-vts-silver/70 hover:text-white"}`}>
                    {p}%
                  </button>
                ))}
                <span className="text-[9px] text-vts-silver/45 font-mono">of balance · compounds every flight</span>
              </>
            ) : (
              <>
                <input data-testid="rocket-server-fixed-input" type="number" min="1" step="0.5" value={fixedStake} disabled={busy}
                  onChange={(e) => { setFixedStake(e.target.value); try { localStorage.setItem("rocketSrvFixed", e.target.value); } catch { /* quota */ } }}
                  className="w-24 rounded-md border border-vts-line bg-vts-surface px-2.5 py-1.5 text-[11px] font-bold font-mono text-white outline-none focus:border-vts-blue" />
                <span className="text-[9px] text-vts-silver/45 font-mono">flat $ every flight · never scales · Deriv accumulator minimum is $1.00</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">🛑 Stop-loss</span>
            {[30, 40, 50].map((p) => (
              <button key={p} data-testid={`rocket-server-sl-${p}`} disabled={busy}
                onClick={() => { setSlPct(p); try { localStorage.setItem("rocketSrvSlPct", String(p)); } catch { /* quota */ } }}
                className={`rounded-md border px-3 py-1.5 text-[11px] font-bold font-mono transition ${
                  slPct === p ? "border-vts-loss bg-vts-loss/20 text-vts-loss" : "border-vts-line bg-vts-surface text-vts-silver/70 hover:text-white"}`}>
                −{p}%
              </button>
            ))}
            <span data-testid="rocket-server-sl-room" className="text-[9px] text-vts-silver/45 font-mono">
              ≈ {Math.max(1, Math.floor(Math.log(1 - slPct / 100) / Math.log(1 - stakePct / 100)))} straight-crash cushion at {stakePct}% stakes (a crash = the full stake)
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">🏦 Secure stops</span>
            {[[true, "ON — bank profits"], [false, "OFF — run non-stop"]].map(([v, label]) => (
              <button key={String(v)} data-testid={`rocket-server-secure-${v ? "on" : "off"}`} disabled={busy}
                onClick={() => { setSecureStops(v); try { localStorage.setItem("rocketSrvSecure", v ? "1" : "0"); } catch { /* quota */ } }}
                className={`rounded-md border px-3 py-1.5 text-[11px] font-bold font-mono transition ${
                  secureStops === v ? (v ? "border-vts-profit bg-vts-profit/20 text-vts-profit" : "border-amber-400 bg-amber-400/15 text-amber-300") : "border-vts-line bg-vts-surface text-vts-silver/70 hover:text-white"}`}>
                {label}
              </button>
            ))}
            <span className="text-[9px] text-vts-silver/45 font-mono">
              {secureStops
                ? "trailing lock + daily target STOP the run to bank wins (recommended)"
                : "⚠️ true 24/7 — never stops for profit; only the stop-loss and 60% breaker can ground it"}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">💰 Daily target</span>
            {[0, 10, 15, 25].map((p) => (
              <button key={p} data-testid={`rocket-server-pt-${p}`} disabled={busy}
                onClick={() => { setPtPct(p); try { localStorage.setItem("rocketSrvPt", String(p)); } catch { /* quota */ } }}
                className={`rounded-md border px-3 py-1.5 text-[11px] font-bold font-mono transition ${
                  ptPct === p ? "border-vts-profit bg-vts-profit/20 text-vts-profit" : "border-vts-line bg-vts-surface text-vts-silver/70 hover:text-white"}`}>
                {p === 0 ? "OFF" : `+${p}%`}
              </button>
            ))}
            <span className="text-[9px] text-vts-silver/45 font-mono">
              {ptPct > 0 ? "bank & park — the run auto-stops and SECURES the win once it's up this much" : "no target — only the trailing lock protects profits"}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">🎯 Target</span>
            <span data-testid="rocket-server-target-band" className="rounded-md border border-amber-400 bg-amber-400/15 px-3 py-1.5 text-[11px] font-bold font-mono text-amber-300">
              🎯 1.02–1.20x
            </span>
            <span className="text-[9px] text-vts-silver/45 font-mono">
              exit re-rolled every flight · accuracy scales the ceiling: choppy pairs bank early ~1.02–1.08x, calm accurate pairs unlock up to 1.20x
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">🎯 Comeback mode</span>
            <button data-testid="rocket-server-comeback-toggle" disabled={busy}
              onClick={() => { const v = !comeback; setComeback(v); try { localStorage.setItem("rocketSrvComeback", v ? "1" : "0"); } catch { /* quota */ } }}
              className={`rounded-md border px-3 py-1.5 text-[11px] font-bold font-mono transition ${
                comeback ? "border-vts-profit bg-vts-profit/20 text-vts-profit" : "border-vts-line bg-vts-surface text-vts-silver/70 hover:text-white"}`}>
              {comeback ? "ON" : "OFF"}
            </button>
            <span className="text-[9px] text-vts-silver/45 font-mono">
              {comeback
                ? "recovery trio: tiny 3% base · crash → THREE equal flights at rolled 1.10–1.25x, together sized to win the WHOLE loss back +15% — each flight risks only ~1/3 · stakes hard-capped at 20% of live balance (big debts chip at max 1.25x until $0) · breaker: 60% → 5h rest → smaller second session"
                : "small stakes + full win-back flights after crashes"}
            </span>
          </div>
          {comeback && (
            <div data-testid="rocket-comeback-recommend" className="rounded-lg border border-sky-400/25 bg-sky-500/5 px-3 py-2 text-[10px] font-mono text-vts-silver/70 leading-snug">
              💡 <b className="text-white">Recommended balance: $50+ (sweet spot $100+)</b> so the ladder has room above
              Deriv's $1 minimum stake — but it's <b className="text-white">optional</b>: every winning flight banks profit
              on its own, and the 🏦 trailing profit lock + park buttons secure what you've made at any size.
            </div>
          )}
          <div data-testid="rocket-server-lock-info" className="rounded-lg border border-vts-profit/25 bg-vts-profit/5 px-3 py-2 text-[10px] font-mono text-vts-silver/70 leading-snug">
            🏦 <b className="text-vts-profit">Trailing profit lock</b> — <b className="text-white">no ceiling</b>: the bot keeps climbing, even
            +100% and beyond. It only stops if the run gives back <b className="text-white">35% of its peak profit</b> (keeps 65%: peaked +40% →
            auto-stops at +26%), securing the rest. Arms once the run is up just 3% of the starting balance. <b className="text-white">Floor guard:</b> once
            armed, NO flight (even recovery) can stake more than the cushion above the locked floor — a crash lands AT the floor, never through it.
          </div>
        </>
      )}
      {paused && (
        <div data-testid="rocket-server-paused" className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] font-mono text-amber-300">
          🔒 Parked until {new Date(st.paused_until).toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })} — locks are final, the server won't restart early.
        </div>
      )}
      {running && (
        <>
          {st?.lock_armed ? (
            <div data-testid="rocket-server-profit-lock" className="rounded-lg border border-vts-profit/30 bg-vts-profit/5 px-3 py-2 text-[10px] font-mono text-vts-silver/70">
              🏦 <b className="text-vts-profit">Trailing lock ARMED:</b> peaked <b className="text-white">+${Number(st.peak_net).toFixed(2)}</b> — auto-secures if profit falls to <b className="text-white">+${Number(st.lock_floor).toFixed(2)}</b>. No ceiling — keep climbing 🚀
            </div>
          ) : (st?.peak_net > 0 && (
            <div data-testid="rocket-server-profit-lock" className="rounded-lg border border-vts-line/30 bg-vts-surface/30 px-3 py-2 text-[10px] font-mono text-vts-silver/60">
              🏦 Trailing lock arms at +{st?.start_balance ? `$${(st.start_balance * 0.03).toFixed(2)}` : "3% of start balance"} — peak so far +${Number(st.peak_net).toFixed(2)}{st?.sl_floor > 0 && <> · 🛑 stop-loss at −${Number(st.sl_floor).toFixed(2)} ({Number(st.sl_pct).toFixed(0)}%)</>}
            </div>
          ))}
          <div data-testid="rocket-server-phase" className="rounded-lg bg-vts-surface/50 border border-vts-line/40 px-3 py-2 min-h-[38px] text-[11px] font-mono text-vts-silver/80 flex items-center gap-2 flex-wrap">
            <span className="text-white">{PHASE[st?.phase] || st?.phase || "…"}</span>
            <span className="text-amber-300">🎯 1.02–1.20x</span>
            {st?.storm_mode && <span data-testid="rocket-server-storm" className="text-amber-400">· ⛈️ storm mode — crashes clustering, banking early ≤1.12x until 6 clean flights</span>}
            {st?.benched?.length > 0 && <span data-testid="rocket-server-benched" className="text-vts-loss/80">· 🚧 dodging spikes on {st.benched.map((b) => pairShort(b.sym)).join(", ")} (10 min bench)</span>}
            {st?.comeback && (st?.pending_recovery > 0
              ? <span data-testid="rocket-server-comeback-armed" className="text-vts-profit">· 🎯 recovery flight #{(st?.recovery_step ?? 0) + 1} — recovering ${Number(st.pending_recovery).toFixed(2)}</span>
              : <span className="text-vts-silver/55">· 🎯 comeback {st?.comeback_wins > 0 || st?.comeback_fails > 0 ? `${st.comeback_wins ?? 0} erased / ${st.comeback_fails ?? 0} missed` : "ready"}</span>)}
            {st?.active_sym_label && <span className="text-vts-blue">· {st.active_sym_label}</span>}
            {st?.last_stake > 0 && (
              <span className="text-vts-silver/55">
                · stake ${Number(st.last_stake).toFixed(2)}
                {st?.last_stake_pct != null
                  ? ` (${st.last_stake_pct}% ${st?.last_flight_kind === "recovery" ? "· 🎯 recovery win-back" : st?.comeback ? "· tiny base" : ""})`
                  : ` (${st?.stake_pct ?? 10}%)`}
              </span>
            )}
            {st?.last_target > 1 && <span className="text-amber-300">· 🎯 {Number(st.last_target).toFixed(2)}x</span>}
            {st?.profit_target_amt > 0 && <span className="text-vts-profit">· 💰 parks at +${Number(st.profit_target_amt).toFixed(2)}</span>}
            {st?.last_error && <span className="text-vts-loss/80">· {st.last_error}</span>}
          </div>
          {st?.comeback && st?.pending_recovery > 0 && st?.recovery_total > 0 && (
            <div data-testid="rocket-server-recovery-progress" className="rounded-lg border border-vts-profit/25 bg-vts-profit/5 px-3 py-2 text-[10px] font-mono">
              <div className="flex items-center justify-between text-vts-silver/70">
                <span>🎯 <b className="text-vts-profit">Recovery in progress</b> — flight #{(st?.recovery_step ?? 0) + 1}</span>
                <span className="text-white">won back ${Math.max(0, st.recovery_total - st.pending_recovery).toFixed(2)} of ${Number(st.recovery_total).toFixed(2)}</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-vts-surface overflow-hidden">
                <div className="h-full rounded-full bg-vts-profit transition-[width] duration-700"
                  style={{ width: `${Math.min(100, Math.max(2, ((st.recovery_total - st.pending_recovery) / st.recovery_total) * 100))}%` }} />
              </div>
              <div className="mt-1 text-[9px] text-vts-silver/50">${Number(st.pending_recovery).toFixed(2)} left — keeps flying until $0, never gives up mid-recovery</div>
            </div>
          )}
          {st?.last_scan?.top?.length > 0 && (
            <div data-testid="rocket-server-scan" className="rounded-lg bg-vts-surface/40 border border-vts-line/30 px-3 py-2 min-h-[52px] text-[10px] font-mono text-vts-silver/65 leading-relaxed">
              🛰️ <b className="text-white">Server's own scan</b> (re-ranked before EVERY flight · {st.last_scan.at ? new Date(st.last_scan.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : ""}):
              {" "}{st.last_scan.top.slice(0, 3).map((r, i) => (
                <span key={r.sym}>{i === 0 ? "⭐" : " ·"} {r.label} ({r.score > 0 ? "+" : ""}{Number(r.score).toFixed(3)})</span>
              ))}
              {st?.pair_switches > 0 && <span className="text-vts-blue"> · switched pair {st.pair_switches}x this run</span>}
            </div>
          )}
          <div className="grid grid-cols-4 gap-2">
            {[["Server P/L", `${(st?.net ?? 0) >= 0 ? "+" : ""}${Number(st?.net ?? 0).toFixed(2)}`, (st?.net ?? 0) >= 0 ? "text-vts-profit" : "text-vts-loss"],
              ["Flights", `${st?.flights ?? 0}`, "text-white"],
              ["W / L", `${st?.wins ?? 0}/${st?.losses ?? 0}`, "text-white"],
              ["Last cash-out", st?.last_mult ? `${Number(st.last_mult).toFixed(2)}x` : "—", "text-amber-300"]].map(([l, v, c]) => (
              <div key={l} data-testid={`rocket-server-stat-${l.replace(/\s|\//g, "-").toLowerCase()}`} className="rounded-lg bg-vts-surface/40 border border-vts-line/30 p-2 text-center">
                <div className={`font-mono text-sm font-bold tabular-nums ${c}`}>{v}</div>
                <div className="text-[8px] uppercase tracking-wider text-vts-silver/50 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        </>
      )}
      {recent.length > 0 && (
        <div>
          <div className="text-[9px] text-vts-silver/45 font-mono mb-1">Server flight log</div>
          <div className="flex flex-wrap gap-1.5">
            {recent.map((t, i) => (
              <span key={t.contract_id || i} data-testid={`rocket-server-flight-${i}`}
                className={`px-2 py-1 rounded-md border text-[10px] font-bold font-mono ${
                  t.profit > 0 ? "border-vts-profit/40 bg-vts-profit/10 text-vts-profit" : "border-vts-loss/40 bg-vts-loss/10 text-vts-loss"}`}>
                {t.comeback ? "🎯" : ""}{pairShort(t.symbol) && <span className="opacity-70">{pairShort(t.symbol)} </span>}{t.ticks ?? "?"}t {t.profit > 0
                  ? `· +${Number(t.profit).toFixed(2)}`
                  : `↑${Number(t.peak_mult ?? t.mult ?? 1).toFixed(2)}x then 💥`}
              </span>
            ))}
          </div>
        </div>
      )}
      {!running && st?.stopped_reason === "profit_target" && (
        <div data-testid="rocket-server-pt-hit" className="rounded-lg border border-vts-profit/50 bg-vts-profit/10 px-3 py-2.5 text-[11px] font-mono">
          <div className="text-vts-profit font-bold">💰 Daily target HIT — banked +{Number(st?.net ?? 0).toFixed(2)} {currency} ({Number(st?.profit_target_pct ?? 0).toFixed(0)}% of the start balance) and parked.</div>
          <div className="text-vts-silver/60 mt-0.5">Quit while green — the win is secured. Flip the toggle whenever you want a fresh run.</div>
        </div>
      )}
      {!running && st?.stopped_reason === "profit_secured" && (Number(st?.net ?? 0) >= 0 ? (
        <div data-testid="rocket-server-secured" className="rounded-lg border border-vts-profit/50 bg-vts-profit/10 px-3 py-2.5 text-[11px] font-mono">
          <div className="text-vts-profit font-bold">🏦 Bot stopped to AUTO-SECURE your profits — banked +{Number(st?.net ?? 0).toFixed(2)} {currency} after peaking at +{Number(st?.peak_net ?? 0).toFixed(2)}.</div>
          <div className="text-vts-silver/60 mt-0.5">The run started giving profits back, so the trailing lock fired — the market can't take the rest. Flip the toggle whenever you want a fresh climb.</div>
        </div>
      ) : (
        <div data-testid="rocket-server-secured" className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2.5 text-[11px] font-mono">
          <div className="text-amber-300 font-bold">🏦 Trailing lock stopped the run at {Number(st?.net ?? 0).toFixed(2)} {currency} after peaking at +{Number(st?.peak_net ?? 0).toFixed(2)}.</div>
          <div className="text-vts-silver/60 mt-0.5">A big recovery crash broke through the old lock floor. This is now fixed: no flight can ever stake more than the cushion above the locked floor — future runs stop AT the floor, in profit.</div>
        </div>
      ))}
      {!running && st?.stopped_reason === "stop_loss" && (
        <div data-testid="rocket-server-sl-hit" className="rounded-lg border border-vts-loss/50 bg-vts-loss/10 px-3 py-2.5 text-[11px] font-mono">
          <div className="text-vts-loss font-bold">🛑 Bot stopped at your stop-loss ({Number(st?.sl_pct ?? 30).toFixed(0)}% of start balance) — net {Number(st?.net ?? 0).toFixed(2)} {currency}.</div>
          <div className="text-vts-silver/60 mt-0.5">The rest of your balance is protected. Rough skies happen — take a breather before a fresh run.</div>
        </div>
      )}
      {!running && st?.stopped_reason && !["profit_secured", "stop_loss", "profit_target"].includes(st.stopped_reason) && !paused && (
        <div data-testid="rocket-server-stopped" className="text-[10px] font-mono text-vts-silver/50">
          last run ended: <b className="text-white">{st.stopped_reason}</b>
          {st?.net != null && <> · net {Number(st.net) >= 0 ? "+" : ""}{Number(st.net).toFixed(2)} {currency} · {st?.flights ?? 0} flights</>}
        </div>
      )}
    </div>
  );
};

export default RocketServerBot;
